export const dateTimeDiff = (date1, date2) => {
  if (date1 === null || date2 === null) {
    return { hours: 0, minutes: 0 };
  }
  const diff = date2.getTime() - date1.getTime();
  const hours = Math.floor(diff / 1000 / 60 / 60);
  const minutes = Math.floor(diff / 1000 / 60) - (hours * 60);
  return { hours, minutes };
};

export const getDateTimeInHHMM = date => {
  if (date === null) {
    return '';
  }
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

export const capitalize = str => {
  if (!str) {
    return '';
  }
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getStringSizeInKB = str => {
  const bytes = new TextEncoder().encode(str).length;
  return bytes / 1024;
};

export const toApiProxyUrl = (url) => {
  const idx = url.indexOf('/api/');
  return idx !== -1 ? url.substring(idx) : url;
};

// Some upstream mail relays double-encode UTF-8 headers (e.g. "TramitaciÃ³n" instead
// of "Tramitación"), so the corrupted bytes arrive as-is from imapserver. Detect the
// classic UTF-8-decoded-as-Latin1 pattern and repair it for display.
export const fixMojibake = str => {
  if (typeof str !== 'string' || !/[ÂÃ][-¿]/.test(str)) {
    return str;
  }
  if ([...str].some(c => c.charCodeAt(0) > 0xFF)) {
    return str;
  }
  try {
    const bytes = Uint8Array.from(str, c => c.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return str;
  }
};

export const repairMessageMojibake = message => {
  if (!message) {
    return message;
  }
  const fixArray = arr => (Array.isArray(arr) ? arr.map(fixMojibake) : arr);
  return {
    ...message,
    from: fixArray(message.from),
    replyTo: fixArray(message.replyTo),
    subject: fixMojibake(message.subject)
  };
};

// Resolve folder for a message using the same logic as message-list
// - If no filters are applied, return the selectedFolder
// - Otherwise, try to match by folderId, then by fullName/name from the exploded items list
export const getExplodedItemsListFromState = state => {
  const explodedItems = state?.folders?.explodedItems;
  if (!explodedItems) {
    return [];
  }
  return Array.isArray(explodedItems) ? explodedItems : Object.values(explodedItems);
};

export const resolveMessageFolder = (message, selectedFolder, filtersApplied, state) => {
  const hasFilters = filtersApplied && Object.keys(filtersApplied).length > 0;
  if (!hasFilters) {
    return selectedFolder || {};
  }
  const list = getExplodedItemsListFromState(state);
  if (message && message.folderId) {
    const byId = list.find(f => f && f.folderId === message.folderId);
    if (byId) {
      return byId;
    }
  }
  // When selected message was refreshed from BE, folder identifiers might live under message.folder
  if (message && message.folder && message.folder.folderId) {
    const byNestedId = list.find(f => f && f.folderId === message.folder.folderId);
    if (byNestedId) {
      return byNestedId;
    }
  }
  if (message && message.folderFullName) {
    const byName = list.find(
      f => f && (f.fullName === message.folderFullName || f.name === message.folderFullName)
    );
    if (byName) {
      return byName;
    }
  }
  if (message && message.folder && (message.folder.fullName || message.folder.name)) {
    const targetName = message.folder.fullName || message.folder.name;
    const byNestedName = list.find(
      f => f && (f.fullName === targetName || f.name === targetName)
    );
    if (byNestedName) {
      return byNestedName;
    }
  }
  return selectedFolder || {};
};

/**
 * Identity key used by the MESSAGES_SET_SELECTED reducer for entries in state.messages.selected:
 * the messageId when present, otherwise the `${subject}||-||${receivedDate}` fallback.
 * Never use the bare IMAP uid for identity — uids are unique per folder, not per account.
 */
export const getSelectionKey = message => {
  if (!message) {
    return null;
  }
  return message.messageId ? message.messageId : `${message.subject}||-||${message.receivedDate}`;
};

/**
 * Given the current selection and the message being unchecked, returns the first message that
 * remains selected (or undefined when none does). Comparison uses getSelectionKey so two
 * messages from different folders sharing the same uid are not confused (PEC-8348).
 */
export const findRemainingSelectedMessage = (selectedMessages, deselectedMessage) => {
  const deselectedKey = getSelectionKey(deselectedMessage);
  return (selectedMessages || []).find(m => getSelectionKey(m) !== deselectedKey);
};

export { xmlToJson } from './document';

export { getVcfField, getPhone } from './vCard';

export const formatSize = (kb) => {
  if (kb >= 1024 * 1024) {
    return (kb / (1024 * 1024)).toFixed(0) + ' GB';
  } else if (kb >= 1024) {
    return (kb / 1024).toFixed(0) + ' MB';
  } else {
    return kb.toFixed(0) + ' KB';
  }
};